import { useEffect, useMemo, useState } from "react";
import { GitBranch } from "lucide-react";
import { EmptyState } from "../ui/empty-state";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../ui/resizable";
import { FlowsSidebar } from "./FlowsSidebar";
import { FlowDetailPanel } from "./FlowDetailPanel";
import { FlowSummaryPanel } from "./FlowSummaryPanel";
import { FlowStepList } from "./FlowStepList";
import { CreateFlowDialog } from "./CreateFlowDialog";
import { CreateFlowStepDialog } from "./CreateFlowStepDialog";
import { CreateFlowEdgeDialog } from "./CreateFlowEdgeDialog";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { toast } from "sonner";
import type { MCPServerConfig } from "@/sdk";
import type { Id } from "@/convex/src/_generated/dataModel";

interface FlowsTabProps {
  serverConfig?: MCPServerConfig;
  serverName?: string;
}

type DetailTab = "overview" | "nodes" | "edges" | "drivers";

export function FlowsTab({ serverConfig, serverName }: FlowsTabProps) {
  const { activeWorkspace } = useAppWorkspace();
  const [selectedFlowId, setSelectedFlowId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");

  // Create Flow Dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>();

  // Create Step Dialog state
  const [isCreateStepOpen, setIsCreateStepOpen] = useState(false);
  const [isCreatingStep, setIsCreatingStep] = useState(false);
  const [createStepError, setCreateStepError] = useState<string | undefined>();

  // Create Edge Dialog state
  const [isCreateEdgeOpen, setIsCreateEdgeOpen] = useState(false);
  const [isCreatingEdge, setIsCreatingEdge] = useState(false);
  const [createEdgeError, setCreateEdgeError] = useState<string | undefined>();

  // Mutations
  const createFlowMutation = useMutation(api.flows.createFlowWithFirstNode);
  const addNodeToFlowMutation = useMutation(api.flows.addNodeToFlow);
  const addEdgeWithDriverMutation = useMutation(api.flows.addEdgeWithDriver);
  const ensureDefaultModules = useMutation(
    api.interactionModules.ensureDefaultInteractionModulesForServer,
  );

  // Query servers to get the appWorkspaceServerId from serverName
  const servers = useQuery(
    api.appWorkspaceServers.listServers,
    activeWorkspace?._id ? { workspaceId: activeWorkspace._id } : "skip",
  );

  // Find the current server by name to get its ID
  const currentServer = useMemo(() => {
    if (!servers || !serverName) return null;
    return servers.find((s) => s.name === serverName) ?? null;
  }, [servers, serverName]);

  // Can only create flows if we have both workspace and server
  const canCreate = !!(activeWorkspace?._id && currentServer?._id);

  // Query flows for the current workspace + server (when present)
  const flows = useQuery(
    api.flows.listFlows,
    activeWorkspace?._id
      ? {
          workspaceId: activeWorkspace._id,
          appWorkspaceServerId: currentServer?._id,
        }
      : "skip",
  );

  const flowsList = flows ?? [];
  const fetchingFlows = flows === undefined;

  // Filter flows by search query
  const filteredFlows = useMemo(() => {
    if (!searchQuery.trim()) return flowsList;
    const query = searchQuery.trim().toLowerCase();
    return flowsList.filter((flow) => {
      const haystack =
        `${flow.name} ${flow.slug} ${flow.description ?? ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [flowsList, searchQuery]);

  // Query flow graph for selected flow
  const flowGraph = useQuery(
    api.flows.getFlowGraph,
    selectedFlowId ? { flowId: selectedFlowId as Id<"flows"> } : "skip",
  );

  const isLoadingFlowGraph = selectedFlowId && flowGraph === undefined;

  // Query interaction modules for the current server (for step creation)
  const interactionModules = useQuery(
    api.interactionModules.listInteractionModulesForServer,
    activeWorkspace?._id && currentServer?._id
      ? {
          workspaceId: activeWorkspace._id,
          appWorkspaceServerId: currentServer._id,
        }
      : "skip",
  );

  const availableModules = interactionModules ?? [];

  // Query canonical widgets for the current server (for edge driver UI component selection)
  const canonicalWidgets = useQuery(
    api.canonicalEntities.listCanonicalWidgetsForServer,
    activeWorkspace?._id && currentServer?._id
      ? {
          workspaceId: activeWorkspace._id,
          appWorkspaceServerId: currentServer._id,
        }
      : "skip",
  );

  const availableWidgets = canonicalWidgets ?? [];

  // Clear selection when server changes
  useEffect(() => {
    setSelectedFlowId("");
    setDetailTab("overview");
  }, [serverName]);

  // Ensure default interaction modules exist for this server
  // This is idempotent - it only creates modules for canonical tools that don't have one
  useEffect(() => {
    if (!activeWorkspace?._id || !currentServer?._id) return;
    ensureDefaultModules({
      workspaceId: activeWorkspace._id,
      appWorkspaceServerId: currentServer._id,
    }).catch((err) => {
      console.error(
        "[Flows] Failed to ensure default interaction modules",
        err,
      );
    });
  }, [activeWorkspace?._id, currentServer?._id, ensureDefaultModules]);

  // Reset detail tab when flow changes
  useEffect(() => {
    setDetailTab("overview");
  }, [selectedFlowId]);

  const handleRefresh = () => {
    // Convex queries auto-refresh reactively
  };

  const handleOpenCreateDialog = () => {
    setCreateError(undefined);
    setIsCreateDialogOpen(true);
  };

  const handleCancelCreate = () => {
    setIsCreateDialogOpen(false);
    setCreateError(undefined);
  };

  const handleCreateFlow = async (data: {
    name: string;
    slug: string;
    description?: string;
  }) => {
    if (!activeWorkspace?._id || !currentServer?._id) {
      setCreateError("Workspace or server not available");
      return;
    }

    setIsCreating(true);
    setCreateError(undefined);

    try {
      const result = await createFlowMutation({
        workspaceId: activeWorkspace._id,
        appWorkspaceServerId: currentServer._id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        status: "draft",
        // Do NOT pass interactionModuleId - creates flow with entryNodeId = null
      });

      // Auto-select the new flow
      if (result.flow?._id) {
        setSelectedFlowId(result.flow._id);
      }

      // Close dialog and show success toast
      setIsCreateDialogOpen(false);
      toast.success("Flow created");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create flow";
      setCreateError(message);
    } finally {
      setIsCreating(false);
    }
  };

  // Step creation handlers
  const handleOpenCreateStep = () => {
    if (!selectedFlowId) {
      toast.error("Please select a flow first");
      return;
    }
    setCreateStepError(undefined);
    setIsCreateStepOpen(true);
  };

  const handleCancelCreateStep = () => {
    setIsCreateStepOpen(false);
    setCreateStepError(undefined);
  };

  const handleCreateStep = async (data: {
    interactionModuleId: string;
    label?: string;
  }) => {
    if (!selectedFlowId) {
      toast.error("Please select a flow first");
      return;
    }

    setIsCreatingStep(true);
    setCreateStepError(undefined);

    try {
      await addNodeToFlowMutation({
        flowId: selectedFlowId as Id<"flows">,
        interactionModuleId:
          data.interactionModuleId as Id<"interactionModules">,
        label: data.label,
        // positionX and positionY left undefined for now
      });

      // Close dialog and show success toast
      setIsCreateStepOpen(false);
      toast.success("Step added");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add step";
      setCreateStepError(message);
    } finally {
      setIsCreatingStep(false);
    }
  };

  // Build nodes array for edge dialog
  const dialogNodes = useMemo(() => {
    if (!flowGraph) return [];
    const { nodes, interactionModules: modules } = flowGraph;
    return nodes.map((node) => {
      const mod = modules.find((m) => m._id === node.interactionModuleId);
      return {
        _id: node._id,
        label: node.label,
        interactionModuleName: mod?.name ?? mod?.slug ?? null,
      };
    });
  }, [flowGraph]);

  // Edge creation handlers
  const handleOpenCreateEdge = () => {
    if (!selectedFlowId) {
      toast.error("Please select a flow first");
      return;
    }
    if (!flowGraph || flowGraph.nodes.length === 0) {
      toast.error("Add at least one step before creating edges");
      return;
    }
    setCreateEdgeError(undefined);
    setIsCreateEdgeOpen(true);
  };

  const handleCancelCreateEdge = () => {
    setIsCreateEdgeOpen(false);
    setCreateEdgeError(undefined);
  };

  const handleCreateEdge = async (data: {
    fromNodeId: string;
    toNodeId?: string | null;
    conditionKind: "always" | "expr_v1";
    conditionExpression?: string;
    driverType: "none" | "designed_response" | "ui_component";
    // designed_response config
    driverAdvice?: string;
    // ui_component config
    canonicalWidgetId?: string;
    propsTemplateJson?: string;
  }) => {
    if (!selectedFlowId || !activeWorkspace?._id || !currentServer?._id) {
      setCreateEdgeError("Missing flow, workspace, or server context");
      return;
    }

    setIsCreatingEdge(true);
    setCreateEdgeError(undefined);

    try {
      const {
        fromNodeId,
        toNodeId,
        conditionKind,
        conditionExpression,
        driverType,
        driverAdvice,
        canonicalWidgetId,
        propsTemplateJson,
      } = data;

      // Map conditionKind
      let edgeConditionKind: string | undefined;
      let edgeConditionConfig: Record<string, unknown> | undefined;
      if (conditionKind === "expr_v1") {
        edgeConditionKind = "expr_v1";
        edgeConditionConfig = { expression: conditionExpression ?? "" };
      } else {
        // "always" - no condition
        edgeConditionKind = undefined;
        edgeConditionConfig = undefined;
      }

      // Map driver config based on type
      let finalDriverType: string | undefined;
      let driverConfig: Record<string, unknown> | undefined;

      if (driverType === "designed_response") {
        finalDriverType = "designed_response";
        driverConfig = {
          advice: driverAdvice ?? "",
        };
      } else if (driverType === "ui_component") {
        finalDriverType = "ui_component";
        // Parse props JSON if provided
        let propsTemplate: Record<string, unknown> | undefined;
        if (propsTemplateJson) {
          try {
            propsTemplate = JSON.parse(propsTemplateJson);
          } catch {
            setCreateEdgeError("Invalid JSON in props");
            setIsCreatingEdge(false);
            return;
          }
        }
        driverConfig = {
          canonicalWidgetId,
          ...(propsTemplate ? { propsTemplate } : {}),
        };
      } else {
        // "none" - no driver
        finalDriverType = undefined;
        driverConfig = undefined;
      }

      await addEdgeWithDriverMutation({
        flowId: selectedFlowId as Id<"flows">,
        fromNodeId: fromNodeId as Id<"flowInteractionModules">,
        toNodeId: toNodeId
          ? (toNodeId as Id<"flowInteractionModules">)
          : undefined,
        conditionKind: edgeConditionKind,
        conditionConfig: edgeConditionConfig,
        priority: undefined, // Let backend default
        driverType: finalDriverType,
        driverName: undefined, // Not exposed in UI
        driverDescription: undefined, // Not exposed in UI
        driverConfig,
      });

      setIsCreateEdgeOpen(false);
      toast.success("Edge added");
      // Convex queries auto-refetch and update the graph
    } catch (err) {
      console.error("[Flows] Failed to add edge", err);
      const message = err instanceof Error ? err.message : "Failed to add edge";
      setCreateEdgeError(message);
    } finally {
      setIsCreatingEdge(false);
    }
  };

  if (!serverConfig) {
    return (
      <EmptyState
        icon={GitBranch}
        title="No Server Selected"
        description="Connect to an MCP server to explore its flows."
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ResizablePanelGroup direction="vertical" className="flex-1">
        {/* Top Row */}
        <ResizablePanel defaultSize={60} minSize={30}>
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Q1: Flows Sidebar */}
            <FlowsSidebar
              flows={flowsList}
              filteredFlows={filteredFlows}
              selectedFlowId={selectedFlowId}
              fetchingFlows={fetchingFlows}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onRefresh={handleRefresh}
              onSelectFlow={setSelectedFlowId}
              onCreateFlow={handleOpenCreateDialog}
              canCreate={canCreate}
            />
            <ResizableHandle withHandle />
            {/* Q2: Flow Detail Panel */}
            <FlowDetailPanel
              flowGraph={flowGraph}
              isLoading={isLoadingFlowGraph}
              detailTab={detailTab}
              onDetailTabChange={setDetailTab}
              onAddStep={handleOpenCreateStep}
              onAddEdge={
                dialogNodes.length > 0 ? handleOpenCreateEdge : undefined
              }
            />
          </ResizablePanelGroup>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Bottom Row */}
        <ResizablePanel defaultSize={40} minSize={15} maxSize={70}>
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Q3: Flow Summary Panel */}
            <FlowSummaryPanel flowGraph={flowGraph} />
            <ResizableHandle withHandle />
            {/* Q4: Flow Step List */}
            <FlowStepList
              flowGraph={flowGraph}
              onAddStep={handleOpenCreateStep}
            />
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Create Flow Dialog */}
      <CreateFlowDialog
        open={isCreateDialogOpen}
        onCancel={handleCancelCreate}
        onSave={handleCreateFlow}
        isSubmitting={isCreating}
        error={createError}
      />

      {/* Create Step Dialog */}
      <CreateFlowStepDialog
        open={isCreateStepOpen}
        onCancel={handleCancelCreateStep}
        onSave={handleCreateStep}
        isSubmitting={isCreatingStep}
        error={createStepError}
        interactionModules={availableModules}
      />

      {/* Create Edge Dialog */}
      <CreateFlowEdgeDialog
        open={isCreateEdgeOpen}
        onCancel={handleCancelCreateEdge}
        onSave={handleCreateEdge}
        isSubmitting={isCreatingEdge}
        error={createEdgeError}
        nodes={dialogNodes}
        canonicalWidgets={availableWidgets}
      />
    </div>
  );
}
